import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { planIntervals } from "@circle-react/helpers/plansHelpers";
import type { PlanIntervals } from "@circle-react/helpers/plansHelpers";
import { useAccountSubscriptionApi } from "@circle-react/hooks/useAccountSubscriptionApi";
import { usePlanStatus } from "@circle-react/hooks/usePlanStatus";
import type { Plan } from "@circle-react/types";
import type { CurrentPlan } from "@circle-react/types";
import { useCurrentPlanApi } from "../hooks/useCurrentPlanApi";
import { useFetchPlans } from "../hooks/usePlansApi";

interface PlanContextProps {
  plans: Plan[];
  isPlansLoading: boolean;
  currentPlan: CurrentPlan;
  isCurrentPlanLoading: boolean;
  isRedesignedPlansPage: boolean;
  billingCycle: PlanIntervals | null;
  billingCurrency: string | null;
  isRenewLoading: boolean;
  shouldRenderBillingCurrency: boolean;
  renewSubscription: any;
  isUpdatingSubscription: boolean;
  updateSubscription: any;
  isListingPlans: boolean;
  refetchPlans: any;
  refetchCurrentPlan: any;
}

const PlansContext = createContext<PlanContextProps | undefined>(undefined);
PlansContext.displayName = "PlansContext";

export const usePlans = (): PlanContextProps => {
  const context = useContext(PlansContext);
  if (!context) {
    throw new Error("usePlans must be used within a PlansProvider");
  }
  return context;
};

interface PlansProviderProps {
  isRedesignedPlansPage?: boolean;
  children: ReactNode;
}

export const PlansProvider = ({
  isRedesignedPlansPage = false,
  children,
}: PlansProviderProps) => {
  const location = useLocation();

  const { currentPlan, isCurrentPlanLoading, refetchCurrentPlan } =
    useCurrentPlanApi();
  const {
    isCommunityOnTrial,
    isExpiredTrial,
    canChooseCurrency: shouldRenderBillingCurrency,
  } = usePlanStatus();

  const getDefaultBillingCycle = useCallback(() => {
    if (isCommunityOnTrial || isExpiredTrial) {
      return planIntervals.year;
    }

    if (!isCurrentPlanLoading && currentPlan) {
      return currentPlan.interval;
    }

    return planIntervals.year;
  }, [isCommunityOnTrial, isCurrentPlanLoading, currentPlan, isExpiredTrial]);

  const [billingCycle, setBillingCycle] = useState<PlanIntervals | null>(null);
  const [locale, setLocale] = useState<string | null>(null);
  const [billingCurrency, setBillingCurrency] = useState<string | null>(null);

  const {
    renewSubscription,
    isRenewLoading,
    updateSubscription,
    isUpdatingSubscription,
  } = useAccountSubscriptionApi();

  const { plans, isPlansLoading, refetchPlans } = useFetchPlans({
    billing_cycle: billingCycle,
    billing_currency: billingCurrency,
    locale: locale,
  });

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const queryBillingCycle = queryParams.get("billing_cycle");
    const queryBillingCurrency = queryParams.get("billing_currency");
    const queryLocale = queryParams.get("locale");

    if (queryBillingCurrency) {
      setBillingCurrency(queryBillingCurrency);
    } else if (currentPlan) {
      setBillingCurrency(null);
    }

    if (queryLocale) {
      setLocale(queryLocale);
    }

    if (
      queryBillingCycle &&
      (queryBillingCycle === planIntervals.month ||
        queryBillingCycle === planIntervals.year)
    ) {
      setBillingCycle(queryBillingCycle);
    } else if (currentPlan) {
      setBillingCycle(getDefaultBillingCycle());
    }
  }, [location, getDefaultBillingCycle, currentPlan]);

  const isListingPlans = isPlansLoading || isCurrentPlanLoading;

  const hasLoadedPlans = plans.length > 0 && !isPlansLoading;
  const firstPlanCurrency = hasLoadedPlans ? plans[0].currency : null;

  const billingCurrencyToDisplay = firstPlanCurrency || billingCurrency;

  return (
    <PlansContext.Provider
      value={{
        plans,
        isPlansLoading,
        currentPlan,
        isCurrentPlanLoading,
        billingCycle,
        isRedesignedPlansPage,
        billingCurrency: billingCurrencyToDisplay,
        renewSubscription,
        isRenewLoading,
        shouldRenderBillingCurrency,
        updateSubscription,
        isUpdatingSubscription,
        refetchPlans,
        refetchCurrentPlan,
        isListingPlans,
      }}
    >
      {children}
    </PlansContext.Provider>
  );
};
